import React from 'react';

/**
 * Transforms CSS properties to CSS custom properties (CSS variables) with React.CSSProperties type casting.
 *
 * @param props - Object containing CSS property names and values
 * @returns Object with CSS custom properties prefixed with "--" and typed as React.CSSProperties
 *
 * @example
 * ```typescript
 * const styles = cssVars({
 *   width: "15px",
 *   "character-width": "15ch"
 * });
 * // Result: { "--width": "15px", "--character-width": "15ch" } as React.CSSProperties
 * ```
 */
export default function cssVars(props: Record<string, string | number | undefined | null>): React.CSSProperties {
    const cssVars: Record<string, string | number | undefined> = {};

    for (const [key, value] of Object.entries(props)) {
        if (value === undefined || value === null) continue;
        cssVars[`--${key}`] = value;
    }

    return cssVars as React.CSSProperties;
}

// #region Tests

if (import.meta.vitest) {
    const { describe, it, expect } = import.meta.vitest;

    describe('cssVars', () => {
        it('should transform CSS properties to CSS custom properties', () => {
            const input = {
                width: '15px',
                'character-width': '15ch',
                height: 20,
            };

            const result = cssVars(input);

            expect(result).toEqual({
                '--width': '15px',
                '--character-width': '15ch',
                '--height': 20,
            });
        });

        it('should handle empty object', () => {
            const result = cssVars({});
            expect(result).toEqual({});
        });

        it('should handle numeric values', () => {
            const input = {
                'z-index': 1000,
                opacity: 0.5,
            };

            const result = cssVars(input);

            expect(result).toEqual({
                '--z-index': 1000,
                '--opacity': 0.5,
            });
        });

        it('should handle kebab-case properties', () => {
            const input = {
                'border-radius': '4px',
                'font-size': '16px',
                'line-height': 1.5,
            };

            const result = cssVars(input);

            expect(result).toEqual({
                '--border-radius': '4px',
                '--font-size': '16px',
                '--line-height': 1.5,
            });
        });

        it('should handle mixed string and number values', () => {
            const input = {
                width: '100%',
                height: 200,
                'max-width': '50vw',
                'min-height': 100,
            };

            const result = cssVars(input);

            expect(result).toEqual({
                '--width': '100%',
                '--height': 200,
                '--max-width': '50vw',
                '--min-height': 100,
            });
        });

        it('should skip undefined and null values', () => {
            const input = {
                width: undefined,
                height: null,
            };

            const result = cssVars(input);
            expect(result).toEqual({});
        });
    });
}

// #endregion
